import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SplitTextContainer = styled.div`
  display: inline-block;
  overflow: hidden;
`;

const Character = styled(motion.span)`
  display: inline-block;
  white-space: pre;
  position: relative;
  
  &.space {
    width: 0.2em;
  }
`;

const SplitText = ({ 
  children, 
  type = "words", 
  stagger = 0.05, 
  delay = 0,
  animation = "slideUp",
  duration = 0.5,
  ease = "easeOut"
}) => {
  const text = children;
  
  const animations = {
    slideUp: {
      hidden: { y: 100, opacity: 0 },
      visible: { y: 0, opacity: 1 }
    },
    slideDown: {
      hidden: { y: -100, opacity: 0 },
      visible: { y: 0, opacity: 1 }
    },
    slideLeft: {
      hidden: { x: 100, opacity: 0 },
      visible: { x: 0, opacity: 1 }
    },
    slideRight: {
      hidden: { x: -100, opacity: 0 },
      visible: { x: 0, opacity: 1 }
    },
    scale: {
      hidden: { scale: 0, opacity: 0 },
      visible: { scale: 1, opacity: 1 }
    },
    rotate: {
      hidden: { rotate: 180, opacity: 0 },
      visible: { rotate: 0, opacity: 1 }
    },
    fade: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 }
    },
    typewriter: {
      hidden: { width: 0, opacity: 0 },
      visible: { width: "auto", opacity: 1 }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: stagger,
        delayChildren: delay
      }
    }
  };

  const characterVariants = {
    hidden: animations[animation]?.hidden || animations.fade.hidden,
    visible: {
      ...animations[animation]?.visible || animations.fade.visible,
      transition: {
        duration,
        ease
      }
    }
  };

  if (type === "characters") {
    return (
      <SplitTextContainer>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {text.split('').map((char, index) => (
            <Character
              key={index}
              variants={characterVariants}
              className={char === ' ' ? 'space' : ''}
            >
              {char}
            </Character>
          ))}
        </motion.div>
      </SplitTextContainer>
    );
  }

  if (type === "words") {
    return (
      <SplitTextContainer>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {text.split(' ').map((word, index) => (
            <motion.span
              key={index}
              variants={characterVariants}
              style={{ display: 'inline-block', marginRight: '0.2em' }}
            >
              {word}
            </motion.span>
          ))}
        </motion.div>
      </SplitTextContainer>
    );
  }

  if (type === "lines") {
    return (
      <SplitTextContainer>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {text.split('\n').map((line, index) => (
            <motion.div
              key={index}
              variants={characterVariants}
              style={{ display: 'block' }}
            >
              {line}
            </motion.div>
          ))}
        </motion.div>
      </SplitTextContainer>
    );
  }

  return <span>{text}</span>;
};

export default SplitText;
